import { useState } from "react";
import PropTypes from "prop-types";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import {
  ResolutionsList,
  ResolutionRow,
  ResolutionSummary,
  ResolutionDetails,
  ResolutionDetailRow,
  MutedText,
} from "../FeeNoticeQueueStyled";
import { formatIsoDate, formatMoney } from "../utils";

/**
 * Existing Salesforce resolutions for this matter — "dates first, amounts
 * behind a dropdown" per the frontend handoff, so the sidebar isn't a wall
 * of dollar figures the reviewer has to parse before they've even
 * confirmed which record they're looking at.
 */
const ResolutionRowItem = ({ resolution, highlighted }) => {
  const [open, setOpen] = useState(false);

  return (
    <ResolutionRow>
      <ResolutionSummary type="button" onClick={() => setOpen((o) => !o)}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {open ? <CaretDown size={13} /> : <CaretRight size={13} />}
          {formatIsoDate(resolution.date)}
          {highlighted && (
            <MutedText style={{ color: "#b45309", fontWeight: 600 }}>
              (matches)
            </MutedText>
          )}
        </span>
        <MutedText>{resolution.referral_partner_name || ""}</MutedText>
      </ResolutionSummary>
      {open && (
        <ResolutionDetails>
          {/* Full Resolution object, mirroring the fields the fee form
              writes — reviewers compare a letter against a complete
              prior record, not a summary (Matthew, 2026-07-24). */}
          {[
            ["Resolution", resolution.name],
            ["Amount", formatMoney(resolution.amount)],
            [
              "Fee %",
              resolution.fee_percent != null ? `${resolution.fee_percent}%` : null,
            ],
            ["Admin Fee", formatMoney(resolution.admin_fee)],
            ["Gross Attorney Fee", formatMoney(resolution.gross_attorney_fee)],
            ["Fees Due to Others", formatMoney(resolution.fees_due_to_others)],
            [
              "Fee Due to Gordon & Partners",
              formatMoney(resolution.fee_due_to_gordon_partners),
            ],
            ["Net Attorney Fee", formatMoney(resolution.net_attorney_fee)],
            ["Fee Due Date", formatIsoDate(resolution.fee_due_date)],
            ["Referral Partner", resolution.referral_partner_name],
          ].map(([label, value]) => (
            <ResolutionDetailRow key={label}>
              <span>{label}</span>
              <span>{value || "—"}</span>
            </ResolutionDetailRow>
          ))}
        </ResolutionDetails>
      )}
    </ResolutionRow>
  );
};

const MatterResolutions = ({ resolutions, duplicateFlags }) => {
  if (!resolutions || resolutions.length === 0) {
    return <MutedText>No existing resolutions for this matter.</MutedText>;
  }

  const matchedSfIds = new Set(
    (duplicateFlags || [])
      .filter((f) => f.kind === "salesforce")
      .map((f) => f.sf_id)
  );

  return (
    <ResolutionsList>
      {resolutions.map((resolution) => (
        <ResolutionRowItem
          key={resolution.sf_id || `${resolution.date}-${resolution.amount}`}
          resolution={resolution}
          highlighted={matchedSfIds.has(resolution.sf_id)}
        />
      ))}
    </ResolutionsList>
  );
};

MatterResolutions.propTypes = {
  resolutions: PropTypes.array,
  duplicateFlags: PropTypes.array,
};

export default MatterResolutions;
